import axios from "axios"; // import value
import { type AxiosInstance, type InternalAxiosRequestConfig, AxiosHeaders, type AxiosResponse, type AxiosError } from "axios"; // import type-only

const API_URL = import.meta.env.VITE_BASE_API_URL || "https://api.themoonhotels.vn/api";

const refreshApi = axios.create({
    baseURL: API_URL,
    headers: { Accept: "application/json" },
    withCredentials: true,
});

class API {
    private api: AxiosInstance;
    constructor() {
        this.api = axios.create({
            baseURL: API_URL,
            headers: {
                Accept: "application/json",
            },
            withCredentials: true,
        });

        this.api.interceptors.request.use(
            (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
                const token = localStorage.getItem("token");
                if (!config.headers) {
                    config.headers = new AxiosHeaders();
                }

                if (token) {
                    config.headers.set("Authorization", `Bearer ${token}`);
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        this.api.interceptors.response.use(
            (response: AxiosResponse) => response.data,
            async (error: AxiosError) => {
                const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
                if (originalRequest && error.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;
                    try {
                        const refreshRes = await refreshApi.post(
                            "/refresh-token",
                            {}, // body rỗng
                            {
                                headers: {
                                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                                },
                            }
                        );
                        const { access_token, user } = refreshRes.data.data;
                        localStorage.setItem('token', access_token);
                        localStorage.setItem('user', JSON.stringify(user));
                        (originalRequest.headers as any)["Authorization"] = `Bearer ${access_token}`;

                        return this.api(originalRequest);
                    } catch (err) {
                        localStorage.removeItem('token');
                        localStorage.removeItem('user');
                        window.location.href = '/login';
                        return Promise.reject(err);
                    }
                }

                if (error.response?.status === 422 || error.response?.status === 400) {
                    return Promise.reject({
                        errors: (error.response?.data as any)?.errors || "Dữ liệu không hợp lệ",
                    });
                }
                return Promise.reject({
                    error: 500,
                    message: "Có lỗi xảy ra, vui lòng thử lại."
                });
            }
        );
    }

    get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
        return this.api.get(endpoint, params ? { params } : undefined);
    }

    post<T>(endpoint: string, data?: any): Promise<T> {
        return this.api.post(endpoint, data);
    }

    put<T>(endpoint: string, data?: any): Promise<T> {
        return this.api.put(endpoint, data);
    }

    delete<T>(endpoint: string): Promise<T> {
        return this.api.delete(endpoint);
    }

    patch<T>(endpoint: string, data?: any): Promise<T> {
        return this.api.patch(endpoint, data);
    }
}

const api = new API();
export default api;
